import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, euro, API, downloadFile } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DataTable from "@/components/DataTable";
import ColumnFilterMenu from "@/components/ColumnFilterMenu";
import MultiSelect from "@/components/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Briefcase, FileSpreadsheet, FileText, ArrowLeft } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHeader, TableHead, TableRow,
} from "@/components/ui/table";
import { HeadCell } from "@/components/SortLabel";
import { useTableSort } from "@/lib/useTableSort";
import { toast } from "sonner";

const empty = { codice: "", nome: "", cliente_id: "", descrizione: "", stato: "aperta", data_inizio: "", listini_ids: [], bef_mode: "fatture" };
const statoColor = {
  aperta: "bg-emerald-50 text-emerald-700 border-emerald-200",
  chiusa: "bg-slate-100 text-slate-600 border-slate-200",
  sospesa: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Commesse() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [colFilters, setColFilters] = useState({});
  const [riepilogo, setRiepilogo] = useState({});
  const [fattureCount, setFattureCount] = useState({});
  const [listini, setListini] = useState([]);

  const load = () => api.get("/commesse").then((r) => setCommesse(r.data));
  useEffect(() => {
    load();
    api.get("/clienti").then((r) => setClienti(r.data));
    api.get("/listini").then((r) => setListini(r.data)).catch(() => {});
    api.get("/kpi/commesse-riepilogo").then((r) => setRiepilogo(r.data)).catch(() => {});
    api.get("/fatture/count-by-commessa").then((r) => setFattureCount(r.data)).catch(() => {});
  }, []);

  const openNew = async () => {
    setEditing(null); setOpen(true);
    let codice = "";
    try { const { data } = await api.get("/commesse/next-codice"); codice = data.codice || ""; } catch (e) {}
    setForm({ ...empty, codice });
  };
  const openEdit = (c) => { setForm(c); setEditing(c.id); setOpen(true); };

  const save = async () => {
    if (!form.codice.trim() || !form.nome.trim()) return toast.error("Codice e nome sono obbligatori");
    if (!form.cliente_id) return toast.error("Seleziona un cliente");
    try {
      const payload = { codice: form.codice, nome: form.nome, cliente_id: form.cliente_id, descrizione: form.descrizione, stato: form.stato, data_inizio: form.data_inizio, listini_ids: form.listini_ids || [], bef_mode: form.bef_mode || "fatture" };
      if (editing) await api.put(`/commesse/${editing}`, payload);
      else await api.post("/commesse", payload);
      toast.success(editing ? "Commessa aggiornata" : "Commessa creata");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/commesse/${id}`);
    toast.success("Commessa eliminata");
    load();
  };

  const download = async (id, kind) => {
    try {
      const res = await api.get(`/commesse/${id}/export/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contabilita.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  };

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clienteFilter = searchParams.get("cliente");
  const clienteObj = clienteFilter ? clienti.find((c) => c.id === clienteFilter) : null;

  const raw = { codice: (c) => c.codice || "", nome: (c) => c.nome || "", cliente: (c) => c.cliente_nome || "", stato: (c) => c.stato || "" };

  const baseRows = commesse.filter((c) => !c.parent_id && (!clienteFilter || c.cliente_id === clienteFilter));

  // Riepilogo importi aggregato (commessa madre = somma di sé + sottocommesse)
  const aggMap = useMemo(() => {
    const m = {};
    commesse.filter((c) => !c.parent_id).forEach((c) => {
      const kids = commesse.filter((x) => x.parent_id === c.id);
      const own = riepilogo[c.id] || {};
      let rend = own.rendicontato || 0, ric = own.riconosciuto || 0, rit = own.ritenuta || 0, rec = own.recuperato || 0;
      kids.forEach((k) => { const r = riepilogo[k.id] || {}; rend += r.rendicontato || 0; ric += r.riconosciuto || 0; rit += r.ritenuta || 0; rec += r.recuperato || 0; });
      m[c.id] = { rend, ric, rit, rec, delta: ric - rend };
    });
    return m;
  }, [commesse, riepilogo]);

  const distinct = useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr)).sort((a, b) => String(a).localeCompare(String(b), "it", { numeric: true }));
    return { codice: uniq(baseRows.map(raw.codice)), nome: uniq(baseRows.map(raw.nome)), cliente: uniq(baseRows.map(raw.cliente)), stato: uniq(baseRows.map(raw.stato)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commesse, clienteFilter]);

  const filtered = baseRows.filter((c) => Object.entries(colFilters).every(([k, set]) => !set || set.has(raw[k](c))));

  const filterFor = (id) => (
    <ColumnFilterMenu testid={id} values={distinct[id]} selected={colFilters[id]}
      onChange={(set) => setColFilters((f) => ({ ...f, [id]: set }))} />
  );

  const columns = [
    { id: "codice", label: "Codice", width: 130, sortable: true, sortValue: (c) => c.codice || "", filter: filterFor("codice"),
      cell: (c) => <span className="font-semibold text-blue-700">{c.codice}</span>,
      footer: () => <span className="uppercase text-[10px] tracking-widest text-slate-500">Totale</span> },
    { id: "nome", label: "Nome", width: 240, sortable: true, sortValue: (c) => c.nome || "", filter: filterFor("nome"),
      cell: (c) => <span className="font-medium text-slate-900">{c.nome}</span> },
    { id: "cliente", label: "Cliente", width: 200, sortable: true, sortValue: (c) => c.cliente_nome || "", filter: filterFor("cliente"),
      cell: (c) => <span className="text-slate-600">{c.cliente_nome || "—"}</span> },
    { id: "stato", label: "Stato", width: 110, sortable: true, sortValue: (c) => c.stato || "", filter: filterFor("stato"),
      cell: (c) => <Badge variant="outline" className={statoColor[c.stato] || statoColor.aperta}>{c.stato}</Badge> },
    { id: "totale", label: "Contabilità", width: 130, align: "right", sortable: true, sortValue: (c) => c.totale || 0,
      cell: (c) => <span className="font-semibold text-blue-800">{euro(c.totale)}</span>,
      footer: (rows) => <span className="text-blue-800">{euro(rows.reduce((s, c) => s + (c.totale || 0), 0))}</span> },
    { id: "riconosciuto", label: "Riconosciuto (BEF)", width: 150, align: "right", sortable: true, sortValue: (c) => aggMap[c.id]?.ric || 0,
      cell: (c) => <span className="font-semibold text-emerald-700">{euro(aggMap[c.id]?.ric || 0)}</span>,
      footer: (rows) => <span className="text-emerald-700">{euro(rows.reduce((s, c) => s + (aggMap[c.id]?.ric || 0), 0))}</span> },
    { id: "delta", label: "Delta", width: 120, align: "right", sortable: true, sortValue: (c) => aggMap[c.id]?.delta || 0,
      cell: (c) => { const d = aggMap[c.id]?.delta || 0; return <span className={`font-semibold ${d < 0 ? "text-rose-600" : "text-emerald-600"}`}>{d > 0 ? "+" : ""}{euro(d)}</span>; },
      footer: (rows) => { const d = rows.reduce((s, c) => s + (aggMap[c.id]?.delta || 0), 0); return <span className={d < 0 ? "text-rose-600" : "text-emerald-600"}>{d > 0 ? "+" : ""}{euro(d)}</span>; } },
    { id: "ritenuta", label: "Ritenute a garanzia", width: 150, align: "right", sortable: true, sortValue: (c) => aggMap[c.id]?.rit || 0,
      cell: (c) => <span className="font-semibold text-amber-700">{euro(aggMap[c.id]?.rit || 0)}</span>,
      footer: (rows) => <span className="text-amber-700">{euro(rows.reduce((s, c) => s + (aggMap[c.id]?.rit || 0), 0))}</span> },
    { id: "recuperato", label: "Ritenute recuperate", width: 150, align: "right", sortable: true, sortValue: (c) => aggMap[c.id]?.rec || 0,
      cell: (c) => { const v = aggMap[c.id]?.rec || 0; return <span className="font-semibold text-emerald-600">{v ? euro(v) : <span className="text-slate-300">—</span>}</span>; },
      footer: (rows) => <span className="text-emerald-600">{euro(rows.reduce((s, c) => s + (aggMap[c.id]?.rec || 0), 0))}</span> },
    { id: "fatture", label: "N° Fatture", width: 110, align: "center", sortable: true,
      sortValue: (c) => { const kids = commesse.filter((x) => x.parent_id === c.id); return kids.length ? kids.reduce((s, k) => s + (fattureCount[k.id] || 0), 0) : (fattureCount[c.id] || 0); },
      cell: (c) => {
        const kids = commesse.filter((x) => x.parent_id === c.id);
        const n = kids.length ? kids.reduce((s, k) => s + (fattureCount[k.id] || 0), 0) : (fattureCount[c.id] || 0);
        return n > 0 ? <span data-testid={`fatture-count-${c.id}`} className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold text-xs">{n}</span> : <span data-testid={`fatture-count-${c.id}`} className="text-slate-300">0</span>;
      },
      footer: (rows) => <span className="text-slate-600">{rows.reduce((s, c) => { const kids = commesse.filter((x) => x.parent_id === c.id); return s + (kids.length ? kids.reduce((a, k) => a + (fattureCount[k.id] || 0), 0) : (fattureCount[c.id] || 0)); }, 0)}</span> },
    { id: "sub", label: "Sottocommesse", width: 150, align: "center", sortable: false,
      cell: (c) => {
        const n = commesse.filter((x) => x.parent_id === c.id).length;
        return <Button variant="outline" size="sm" data-testid={`open-sub-${c.id}`} onClick={() => navigate(`/commesse/${c.id}`)} className="h-8 text-xs">{n > 0 ? `${n} sottocommess${n > 1 ? "e" : "a"}` : "Gestisci"}</Button>;
      } },
    { id: "azioni", label: "Azioni", width: 160, align: "right", sortable: false,
      cell: (c) => (
        <div className="flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="icon" title="Esporta Excel" data-testid={`export-excel-${c.id}`} onClick={() => download(c.id, "excel")} className="h-8 w-8 text-slate-500 hover:text-emerald-600"><FileSpreadsheet className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title="Esporta PDF" data-testid={`export-pdf-${c.id}`} onClick={() => download(c.id, "pdf")} className="h-8 w-8 text-slate-500 hover:text-rose-600"><FileText className="h-4 w-4" /></Button>
          {isAdmin && (
          <Button variant="ghost" size="icon" data-testid={`edit-commessa-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 text-slate-500 hover:text-blue-700"><Pencil className="h-4 w-4" /></Button>
          )}
          {isAdmin && (
          <ConfirmDelete title="Eliminare commessa?" description="Verranno eliminate anche tutte le tratte e le righe di contabilità collegate." actionTestid={`confirm-delete-commessa-${c.id}`} onConfirm={() => remove(c.id)}>
            <Button variant="ghost" size="icon" data-testid={`delete-commessa-${c.id}`} className="h-8 w-8 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
          )}
        </div>) },
  ];

  return (
    <>
      

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-4">
        <PageHeader title="Commesse" subtitle="Progetti suddivisi per cliente">
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="export-commesse-button"
            onClick={() => downloadFile("/kpi/commesse-export", "commesse.xlsx", { ids: filtered.map((c) => c.id).join(",") })}
            className="bg-white">
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-700" /> Esporta elenco
          </Button>
          {isAdmin && (
          <Button data-testid="add-commessa-button" onClick={openNew} className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova commessa
          </Button>
          )}
        </div>
      </PageHeader>
        {clienteObj && (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="cliente-filter-breadcrumb">
            <Button variant="ghost" size="sm" data-testid="back-to-all-commesse" onClick={() => navigate("/commesse")} className="text-slate-600 hover:text-blue-700 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Tutte le commesse
            </Button>
            <span className="text-slate-300">/</span>
            <span className="text-slate-700">Cliente: <strong className="font-heading text-slate-900">{clienteObj.nome}</strong></span>
            <span className="text-xs text-slate-400">· {filtered.length} commesse</span>
          </div>
        )}
        {commesse.length === 0 ? (
          <Card className="rounded-md border-slate-200 shadow-sm"><Empty onAdd={openNew} /></Card>
        ) : (
          <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
            {filtered.length === 0 && Object.values(colFilters).some(Boolean) && (
              <div className="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-800">
                <span>Nessuna commessa corrisponde ai filtri.</span>
                <Button variant="outline" size="sm" data-testid="reset-filtri-commesse" onClick={() => setColFilters({})} className="h-7 text-xs bg-white">Azzera filtri</Button>
              </div>
            )}
            <DataTable tableKey="commesse" columns={columns} rows={filtered} rowKey={(c) => c.id}
              rowTestId={(c) => `commessa-row-${c.id}`} containerClassName="max-h-[calc(100vh-260px)]"
              emptyText="Nessuna commessa corrisponde ai filtri."
              initialSort={{ key: "codice", dir: "asc" }} />
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifica commessa" : "Nuova commessa"}</DialogTitle>
            <DialogDescription>Dati della commessa. I listini si associano alle contabilità.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700 text-sm">Codice *</Label>
                <Input data-testid="commessa-codice-input" value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} className="mt-1.5" placeholder="C-001" />
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Stato</Label>
                <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                  <SelectTrigger data-testid="commessa-stato-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aperta">Aperta</SelectItem>
                    <SelectItem value="sospesa">Sospesa</SelectItem>
                    <SelectItem value="chiusa">Chiusa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Nome *</Label>
              <Input data-testid="commessa-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Cliente *</Label>
              <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                <SelectTrigger data-testid="commessa-cliente-select" className="mt-1.5"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>
                  {clienti.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Descrizione</Label>
              <Input data-testid="commessa-descrizione-input" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Listino associato</Label>
              <div className="mt-1.5">
                <MultiSelect testid="commessa-listino-select" placeholder="Nessun listino"
                  value={form.listini_ids || []} onChange={(v) => setForm({ ...form, listini_ids: v })}
                  options={listini.map((l) => ({ value: l.id, label: `${l.nome}${l.tipo === "subappalto" ? " · subappalto" : ""}` }))} />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">Il listino viene ereditato dalle sottocommesse (gli amministratori possono modificarlo per singola sottocommessa).</p>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Riconoscimento BEF</Label>
              <Select value={form.bef_mode || "fatture"} onValueChange={(v) => setForm({ ...form, bef_mode: v })}>
                <SelectTrigger data-testid="commessa-befmode-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fatture">Da fatture associate</SelectItem>
                  <SelectItem value="pagato">Manuale: "Pagato" per riga</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1.5">In modalità "Pagato per riga" il Riconosciuto (BEF) somma solo le righe di contabilità marcate come pagate; le fatture servono solo a catalogare il fatturato.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-commessa-button" onClick={save} className="bg-blue-700 hover:bg-blue-800">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Empty = ({ onAdd }) => (
  <div className="py-20 flex flex-col items-center text-center">
    <div className="h-14 w-14 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
      <Briefcase className="h-6 w-6 text-slate-400" />
    </div>
    <p className="text-slate-600 font-medium">Nessuna commessa</p>
    <p className="text-sm text-slate-400 mt-1 mb-4">Crea una commessa e assegnala a un cliente.</p>
    <Button onClick={onAdd} variant="outline"><Plus className="h-4 w-4 mr-1.5" /> Nuova commessa</Button>
  </div>
);
